import { MutableRefObject, ReactNode, useRef } from 'react';
import clsx from 'clsx';

import { Button, Icon } from '@components';
import { ButtonProps } from '@types';

type ModalSize = 'default' | 'wide';

interface ModalProps {
  onCancel?: () => void;
  isOpen: boolean;
  className?: string;
  children: ReactNode;
  isCloseIconRequired?: boolean;
  modalHeader?: string;
  headerClassName?: string;
  modalBodyClassName?: string;
  /** Use `wide` for content such as the desktop result view. */
  size?: ModalSize;
  isPrimaryButtonRequired?: boolean;
  isSecondaryButtonRequired?: boolean;
  isTertiaryButtonRequired?: boolean;
  primaryButtonProps?: ButtonProps;
  secondaryButtonProps?: ButtonProps;
  tertiaryButtonProps?: ButtonProps;
}

const Modal = ({
  onCancel,
  isOpen,
  className = '',
  children,
  modalHeader = '',
  headerClassName,
  modalBodyClassName,
  size = 'default',
  isCloseIconRequired = false,
  primaryButtonProps,
  secondaryButtonProps,
  tertiaryButtonProps,
  isPrimaryButtonRequired = false,
  isSecondaryButtonRequired = false,
  isTertiaryButtonRequired = false,
  ...props
}: ModalProps) => {
  const modalRef = useRef() as MutableRefObject<HTMLDivElement>;
  const isWide = size === 'wide';

  return (
    <div
      className={clsx(
        'fixed left-0 top-0 z-50 flex size-full justify-center backdrop-blur-[15px] transition-opacity duration-300 ease-in-out',
        isWide
          ? 'items-center overflow-hidden p-2 sm:p-4'
          : 'custom-scroll-bar items-start',
        isOpen ? 'opacity-100' : 'hidden opacity-0'
      )}
      {...props}
    >
      {/* Modal Overlay */}
      <div className="bg-black/40 absolute size-full opacity-30" />

      {/* Modal Main Content */}
      <div
        className={clsx(
          'relative my-auto rounded-3xl bg-primaryLite shadow-lg',
          isWide
            ? 'mx-0 flex h-[min(850px,calc(100dvh-1rem))] w-full max-w-[1100px] flex-col overflow-hidden sm:h-[min(850px,calc(100dvh-2rem))]'
            : 'mx-4 sm:m-auto',
          className
        )}
        ref={modalRef}
      >
        {modalHeader && (
          <div
            className={clsx(
              'flex items-center overflow-hidden',
              modalHeader ? 'justify-between' : 'justify-end'
            )}
          >
            {modalHeader && (
              <h1 className={headerClassName || ''}>{modalHeader}</h1>
            )}
            {isCloseIconRequired && (
              <Button variant="transparent" handleClick={onCancel}>
                <Icon name="close" />
              </Button>
            )}
          </div>
        )}
        {isCloseIconRequired && !modalHeader && (
          <Button
            variant="transparent"
            handleClick={onCancel}
            className="absolute right-0 top-4 mr-2"
          >
            <Icon name="close" />
          </Button>
        )}

        <div
          className={clsx(
            modalBodyClassName,
            isWide && 'flex min-h-0 flex-1 flex-col overflow-hidden'
          )}
        >
          {children}
        </div>

        {(isSecondaryButtonRequired ||
          isTertiaryButtonRequired ||
          isPrimaryButtonRequired) && (
          <div className="flex items-center justify-end gap-2 p-6 pt-2">
            {isSecondaryButtonRequired && (
              <Button variant="secondary" {...secondaryButtonProps} />
            )}
            {isTertiaryButtonRequired && (
              <Button variant="tertiary" {...tertiaryButtonProps} />
            )}
            {isPrimaryButtonRequired && (
              <Button variant="primary" {...primaryButtonProps} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
